import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Image } from '@sunwebgroup/uds-ui/image'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import cn from 'classnames'
import { type ComponentPropsWithoutRef, type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { CMSMedia, CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { handleKeyDown } from '@/helpers/keyboardNavigation'
import { useIntl } from '@/intl'
import { FullScreenGallery } from '@/scopes/common/components/FullScreenGallery'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { MediaServerImage } from '@/scopes/common/components/MediaServerImage'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useAddFavoriteFromUrl } from '@/scopes/favourites/domain/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { FavoriteButton } from '@/scopes/products/pages/components/HeroSection/Gallery/Favorite/FavoriteButton'
import styles from './Gallery.module.css'

const MAX_HERO_IMAGE_SIZE = 668
const MAX_GALLERY_IMAGE_SIZE = 470

interface GalleryProps {
  props?: ComponentPropsWithoutRef<'ol'>
}

interface GalleryModalItemProps {
  mediaElement: CMSMedia
  index: number
  onDetailImageClick: (index: number) => void
  renderPlayVideoIcon: (mediaType: CMSMediaType) => ReactNode
}

interface SentinelProps {
  onVisible: () => void
  className?: string
}

/**
 * A sentinel component that triggers a callback when it becomes visible on the screen.
 * @param onVisible - The callback function to be called when the sentinel becomes visible.
 * @returns The sentinel component.
 */
function Sentinel({ onVisible, className }: SentinelProps) {
  const sentinelRef = useRef<HTMLLIElement>(null)
  const isSentinelVisible = useOnScreen(sentinelRef)

  useEffect(() => {
    if (isSentinelVisible) {
      onVisible()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We know onVisible is not going to change.
  }, [isSentinelVisible])

  return <li ref={sentinelRef} className={className} />
}

function toCssVariable(tokenName: string): string {
  return `var(--${tokenName.replaceAll('_', '-')})`
}

export function ImageElementButton({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div {...props} role="button" tabIndex={0} className={cn(styles.imageElementButton, className)} />
}

function GalleryElement({ className, ...props }: ComponentPropsWithoutRef<'ol'>) {
  return <ol {...props} className={cn(styles.wrapper, className)} />
}

function GalleryModalItem({ mediaElement, index, onDetailImageClick, renderPlayVideoIcon }: GalleryModalItemProps) {
  const intl = useIntl()
  const itemRef = useRef<HTMLDivElement>(null)
  const isInView = useOnScreen(itemRef)
  const GalleryImage = isInView ? MediaServerImage : MediaServerLazyImage

  return (
    <li className={styles.imageWrapperModal}>
      <div
        className={styles.imageElementButtonModal}
        role="button"
        tabIndex={0}
        onClick={() => onDetailImageClick(index)}
        onKeyDown={e => handleKeyDown(e, () => onDetailImageClick(index))}
        ref={itemRef}
        aria-label={intl.formatMessage({ id: 'photo_gallery_open_in_gallery' })}
      >
        <div id={`gallery-element-${index}`} />
        <GalleryImage
          src={mediaElement.src}
          widths={index % 3 === 0 ? [1280, 960, 480] : [960, 480]}
        />
        {renderPlayVideoIcon(mediaElement.type)}
      </div>
    </li>
  )
}

export default function Gallery({ props }: GalleryProps) {
  const intl = useIntl()
  const media = useCMSPageField('PDP.media')
  const collectionName = useCMSPageField('PDP.collectionName')
  const collectionTheme = useCMSPageField('PDP.collectionTheme')
  const contextItemId = useCMSPageField('contextItemId')

  useAddFavoriteFromUrl(contextItemId)

  const maxElements = 5
  const modalTitle = intl.formatMessage({ id: 'accommodation_page_gallery_modal_title' })
  const [showModal, setShowModal] = useState(false)
  const [activeItem, setActiveItem] = useState(0)
  const [showDetail, setShowDetail] = useState(false)
  const [clickedItemIndex, setClickedItemIndex] = useState(0)
  const { sendEvent } = useAnalytics()

  const pillCaption: string = intl.formatMessage(
    {
      id:
        media.length > 1
          ? 'accommodation_page_gallery_pill_caption_plural'
          : 'accommodation_page_gallery_pill_caption_singular',
    },
    { num_elements: media.length },
  )

  useEffect(() => {
    if (showModal) {
      window.requestAnimationFrame(() => {
        const element = document.getElementById(`gallery-element-${activeItem}`)
        if (element) {
          element.scrollIntoView()
        }
      })
    }
  }, [showModal])

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = (index: number) => {
    setShowModal(true)
    setActiveItem(index)
  }

  const closeDetail = () => {
    setShowDetail(false)
  }

  const openDetail = (index: number) => {
    setClickedItemIndex(index)
    setShowDetail(true)
  }

  const firstMedia = media.slice(0, maxElements)

  const handleImageClick = (index: number) => {
    openModal(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: `Photo Gallery`,
        result: 'Collection gallery - Opens',
        type: `${index + 1}`,
      }),
    )
  }

  const handleDetailImageClick = (index: number) => {
    openDetail(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: `Photo Gallery`,
        result: 'Image slider - Opens',
        type: `${index + 1}`,
      }),
    )
  }

  const handleSentinelVisible = (): void => {
    sendEvent(
      createPDPClickEvent({
        element: 'Scroll',
        description: `Photo Gallery`,
        result: 'Collection gallery - Scroll to bottom',
      }),
    )
  }

  const handleOnSettleDetail = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: `Photo Gallery`,
        result: 'Image slider - Slide',
      }),
    )
  }

  const renderPlayVideoIcon = (mediaType: CMSMediaType) => {
    if (mediaType === CMSMediaType.VIDEO) {
      return (
        <Icon
          className={styles.playIcon}
          glyph="Play"
          aria-label={intl.formatMessage({ id: 'photo_gallery_play_video' })}
        />
      )
    }

    return null
  }

  if (!media || media.length === 0)
    return null

  return (
    <>
      <GalleryElement {...props} className={cn('accommodation-carousel', props?.className)}>
        {firstMedia.map((element, index) => {
          const isHeroImage = index === 0
          return (
            <li className={styles.mainGridWrapper} key={`gallery-first-media-${index}`}>
              <ImageElementButton
                onClick={() => handleImageClick(index)}
                aria-label={modalTitle}
                onKeyDown={e => handleKeyDown(e, () => handleImageClick(index))}
              >
                <Image
                  className={styles.imageElement}
                  src={buildImgSrc(
                    element.src,
                    isHeroImage
                      ? MAX_HERO_IMAGE_SIZE
                      : MAX_GALLERY_IMAGE_SIZE,
                  )}
                  loading="eager"
                  fetchPriority="high"
                  srcSet={
                    isHeroImage
                      ? buildImgSrcSet(element.src, [MAX_HERO_IMAGE_SIZE, 608])
                      : buildImgSrcSet(element.src, [MAX_GALLERY_IMAGE_SIZE, 330, 304])
                  }
                  alt={element.alt || ''}
                />
                {renderPlayVideoIcon(element.type)}
                {isHeroImage && collectionName && (
                  <Tag
                    className={styles.collectionLabel}
                    size="M"
                    style={{
                      '--_collection-background-color': toCssVariable(collectionTheme.background),
                      '--_collection-text-color': toCssVariable(collectionTheme.text),
                    } as CSSProperties}
                  >
                    {collectionName}
                  </Tag>
                )}
              </ImageElementButton>
              {index === 0 && (
                <>
                  <Button as="span" className={styles.galleryButton} size="S" variant="overlayNeutral">
                    <Button.Icon glyph="Image" size={24} />
                    {pillCaption}
                  </Button>
                  <div className={styles.favoriteContainer}>
                    <FavoriteButton />
                  </div>
                </>
              )}
            </li>
          )
        })}
      </GalleryElement>
      <Sheet.Root open={showModal} onOpenChange={closeModal}>
        <Sheet.Body side="bottom">
          <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={modalTitle} />
          <Sheet.Content>
            <ol className={styles.gridModal}>
              {media.map((mediaElement, index) => (
                <GalleryModalItem
                  key={`gallery-media-${index}`}
                  mediaElement={mediaElement}
                  index={index}
                  onDetailImageClick={handleDetailImageClick}
                  renderPlayVideoIcon={renderPlayVideoIcon}
                />
              ))}
              <Sentinel className={styles.sentinel} onVisible={handleSentinelVisible} />
            </ol>
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
      <FullScreenGallery onClose={closeDetail} open={showDetail} startFromIndex={clickedItemIndex} media={media} onSettle={handleOnSettleDetail} />
    </>
  )
}

Gallery.GalleryElement = GalleryElement
